// Centralised session/token handling for the admin dashboard.
//
// Full XSS resistance requires the API to issue the JWT as an httpOnly cookie
// (see REACT_APP_AUTH_USE_HTTPONLY_COOKIE). Until then, the access token is kept
// in sessionStorage (tab-scoped) rather than localStorage.

export const HERO_API_BASE =
  process.env.REACT_APP_HERO_API_URL ||
  "";

export const heroApiUrl = (path) => `${HERO_API_BASE}${path}`;

/** When true, session JWT is expected on httpOnly cookie; client does not store the token. */
export const AUTH_USE_HTTPONLY_COOKIE =
  process.env.REACT_APP_AUTH_USE_HTTPONLY_COOKIE === "true";

export const TOKEN_KEY = "ft9jaAdminToken";
export const EMAIL_KEY = "ft9jaAdminEmail";
export const ADMIN_KEY = "ft9jaPromo"; // legacy key name kept for compatibility

let memoryToken = null;

const readStoredToken = () => {
  if (AUTH_USE_HTTPONLY_COOKIE) return null;
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const getToken = () => {
  if (AUTH_USE_HTTPONLY_COOKIE) return null;
  return memoryToken ?? readStoredToken();
};

export const getEmail = () => {
  try {
    return sessionStorage.getItem(EMAIL_KEY);
  } catch {
    return null;
  }
};

export const isAdmin = () => {
  try {
    return sessionStorage.getItem(ADMIN_KEY) === "true";
  } catch {
    return false;
  }
};

export const isAuthed = () => {
  if (AUTH_USE_HTTPONLY_COOKIE) {
    return isAdmin();
  }
  return !!getToken() && isAdmin();
};

export const setSession = ({ token, email, isAdmin: adminFlag } = {}) => {
  try {
    if (!AUTH_USE_HTTPONLY_COOKIE && token) {
      memoryToken = token;
      sessionStorage.setItem(TOKEN_KEY, token);
    }
    if (email) sessionStorage.setItem(EMAIL_KEY, email);
    if (adminFlag !== undefined) {
      sessionStorage.setItem(ADMIN_KEY, String(!!adminFlag));
    }
  } catch {
    // sessionStorage may be unavailable in rare privacy modes
  }
};

export const clearSession = () => {
  memoryToken = null;
  try {
    [TOKEN_KEY, ADMIN_KEY, EMAIL_KEY].forEach((k) =>
      sessionStorage.removeItem(k)
    );
    // Clear legacy localStorage session from older builds
    [TOKEN_KEY, ADMIN_KEY, EMAIL_KEY].forEach((k) =>
      localStorage.removeItem(k)
    );
  } catch {
    /* ignore */
  }
};

export const logout = () => {
  clearSession();
};

export const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const resolveHeroApiHost = (base) => {
  if (base) {
    try {
      return new URL(base).host;
    } catch {
      return "";
    }
  }
  if (typeof window !== "undefined" && window.location?.host) {
    return window.location.host;
  }
  return "";
};

export const HERO_API_HOST = resolveHeroApiHost(HERO_API_BASE);

const requestUrl = (config) => {
  const base = (config && config.baseURL) || "";
  const path = (config && config.url) || "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

const targetsHeroApi = (config) => {
  if (!HERO_API_HOST) return false;
  try {
    const resolved = requestUrl(config);
    const base =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    return new URL(resolved, base).host === HERO_API_HOST;
  } catch {
    return `${(config && config.baseURL) || ""}${(config && config.url) || ""}`.includes(
      HERO_API_HOST
    );
  }
};

export const installAuthInterceptors = (client) => {
  client.defaults.withCredentials = AUTH_USE_HTTPONLY_COOKIE;

  client.interceptors.request.use((config) => {
    const token = getToken();
    if (token && targetsHeroApi(config)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (AUTH_USE_HTTPONLY_COOKIE && targetsHeroApi(config)) {
      config.withCredentials = true;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error &&
        error.response &&
        error.response.status === 401 &&
        targetsHeroApi(error.config)
      ) {
        clearSession();
        const path = window.location.pathname || "";
        if (path !== "/") {
          window.location.href = "/";
        }
      }
      return Promise.reject(error);
    }
  );
};
